import { useCallback } from 'react'
import { useQuery, useReactiveVar } from '@apollo/client'
import { activeArtistVar, activeProjectVar, labelCodeIdVar } from 'modulesCommon/store'
import get from 'lodash/get'

import { GET_ARTIST_PRODUCTS } from '../constants'
import { IGlobalProduct, IProductsByCampaign } from '../types'
import { IProject, IArtist } from 'shared/interfaces'

interface IMeta {
  campaign_id: string
  id: string
}

function concatenate(...props: ReadonlyArray<string | number>) {
  return props.join('.')
}

function forbiddenListAdapter(include: string) {
  return (data: IGlobalProduct) => {
    const prefix = concatenate(data.configuration, data.name)
    return data.locals.reduce<Record<string, IMeta>>(
      (a, v) =>
        v.label_id === include
          ? {
            ...a,
            [concatenate(prefix, v.label_id)]: { campaign_id: v.campaign_id, id: data.id }
          }
          : a,
      {}
    )
  }
}

export const useValidation = () => {
  const activeArtist = useReactiveVar<IArtist>(activeArtistVar)
  const activeProject = useReactiveVar<Omit<IProject, 'artist'>>(activeProjectVar)
  const currentLabelId = useReactiveVar(labelCodeIdVar)

  const { data } = useQuery<{ getArtistProducts: IGlobalProduct[] }>(GET_ARTIST_PRODUCTS, {
    variables: {
      name: '',
      artist_id: activeArtist?.id
    }
  })

  const forbiddenDictionary = useCallback<(excludeId: string) => Record<string, IMeta>>(
    (excludeId) => {
      if (typeof data?.getArtistProducts !== 'undefined') {
        const forbiddenList = forbiddenListAdapter(currentLabelId)
        return data.getArtistProducts.reduce((a, v) => (v.id !== excludeId ? { ...a, ...forbiddenList(v) } : a), {})
      }
      return []
    },
  [currentLabelId, data]
  )

  const handleValidate = useCallback(
    ({ name, configuration, parent_id }: IProductsByCampaign) => {
      const res = get(forbiddenDictionary(parent_id), concatenate(configuration, name, activeProject.label_id))
      if (typeof res !== 'undefined') return `The product already exists in project ${res.campaign_id}`
      return
    },
    [activeProject.label_id, forbiddenDictionary]
  )

  return {
    handleValidate
  }
}
